import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import ReceiptIcon from '@mui/icons-material/Receipt';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import PrintIcon from '@mui/icons-material/Print';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { alpha } from '@mui/material/styles';
import firebase from '../../../utilities/firebase';
import { StateManager } from '../../../utilities/stateManager';
import DepositModal from '../../../components/DepositModal';
import EditDepositModal from '../../../components/EditDepositModal';
import SavedCardsList from '../../../components/SavedCardsList';
import CreditCardAffidavitButton from '../../../components/CreditCardAffidavitButton';
import RequestPaymentButton from '../../../components/RequestPaymentButton';
import moment from 'moment';

const USD = (v) =>
  (v || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 });

export default function DepositsPanel({ deposits = [], stockNumber, invoiceTotal = 0, disabled, car = {}, onDepositAdded, onDepositDeleted, onDepositUpdated }) {
  const [depositModalOpen, setDepositModalOpen] = React.useState(false);
  const [editDeposit, setEditDeposit] = React.useState(null);
  const [printMenuAnchor, setPrintMenuAnchor] = React.useState(null);
  const isAdmin = StateManager.isAdmin();

  const openDepositReceipt = (depositId) => {
    window.open(`/receipt/deposit/${depositId}/print`, '_blank');
  };
  const openStatement = () => {
    setPrintMenuAnchor(null);
    window.open(`/receipt/car/${stockNumber}/statement/print`, '_blank');
  };
  const openInvoice = () => {
    setPrintMenuAnchor(null);
    window.open(`/receipt/car/${stockNumber}/invoice/print`, '_blank');
  };

  const items = deposits;
  const total = items.reduce((s, d) => s + (d.amount || 0), 0);
  const remaining = invoiceTotal - total;
  const progress = invoiceTotal > 0 ? Math.min((total / invoiceTotal) * 100, 100) : 0;
  const paidInFull = invoiceTotal > 0 && remaining <= 0;

  const viewReceipt = async (fileLocation) => {
    const url = await firebase.storage().ref(fileLocation).getDownloadURL();
    window.open(url, '_blank');
  };

  const deleteDeposit = async (id) => {
    if (!window.confirm('Delete this deposit?')) return;
    StateManager.setLoading(true);
    await firebase.firestore().collection('deposits').doc(id).delete();
    if (onDepositDeleted) onDepositDeleted(id);
    StateManager.setLoading(false);
  };

  const addDeposit = () => setDepositModalOpen(true);

  return (
    <Box>
      {/* Progress bar */}
      {invoiceTotal > 0 && (
        <Box sx={{ px: 2, pt: 2, pb: 1.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75, alignItems: 'baseline' }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
              {USD(total)} collected
            </Typography>
            <Typography
              variant="caption"
              sx={{ fontWeight: 700, color: paidInFull ? 'success.main' : 'warning.dark' }}
            >
              {paidInFull ? 'Paid in full' : `${USD(remaining)} remaining`}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 7, borderRadius: 4,
              bgcolor: 'grey.200',
              '& .MuiLinearProgress-bar': {
                borderRadius: 4,
                bgcolor: paidInFull ? 'success.main' : 'primary.main',
              },
            }}
          />
        </Box>
      )}

      {/* Deposit rows */}
      {items.length > 0 ? (
        <Box sx={{ mx: 2, mb: 1.5, border: 1, borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden' }}>
          {items.map((dep, i) => (
            <Box
              key={dep.id || i}
              onClick={isAdmin && !disabled ? () => setEditDeposit(dep) : undefined}
              sx={{
                px: 2, py: 1.25,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                borderBottom: i < items.length - 1 ? 1 : 0, borderColor: 'divider',
                bgcolor: i % 2 === 1 ? 'grey.50' : 'transparent',
                cursor: isAdmin && !disabled ? 'pointer' : 'default',
                '&:hover': isAdmin && !disabled ? { bgcolor: 'action.hover' } : {},
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{USD(dep.amount)}</Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {dep.date ? moment(dep.date, ['MM-DD-YYYY', 'YYYY-MM-DD', 'YYYY/MM/DD']).format('MMM D, YYYY') : '—'}
                  {dep.memo ? ` · ${dep.memo}` : ''}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}>
                {dep.id && (
                  <IconButton size="small" title="Print payment receipt" data-sold-ok onClick={(e) => { e.stopPropagation(); openDepositReceipt(dep.id); }}>
                    <PrintIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                )}
                {dep.files?.[0] && (
                  <IconButton size="small" title="View attached file" data-sold-ok onClick={(e) => { e.stopPropagation(); viewReceipt(dep.files[0]); }} disabled={disabled}>
                    <ReceiptIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                )}
                <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); deleteDeposit(dep.id); }} disabled={disabled}>
                  <DeleteIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            </Box>
          ))}
        </Box>
      ) : (
        <Box sx={{ px: 2, pt: invoiceTotal > 0 ? 0 : 2, pb: 1 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>No deposits recorded</Typography>
        </Box>
      )}

      {/* Actions */}
      <Box sx={{ px: 2, pb: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={addDeposit} disabled={disabled}>
          Add Deposit
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<RequestQuoteIcon />}
          onClick={(e) => setPrintMenuAnchor(e.currentTarget)}
          data-sold-ok
        >
          Print Receipt
        </Button>
        <Menu
          anchorEl={printMenuAnchor}
          open={!!printMenuAnchor}
          onClose={() => setPrintMenuAnchor(null)}
        >
          <MenuItem onClick={openStatement}>Payment Statement</MenuItem>
          <MenuItem onClick={openInvoice}>Full Invoice</MenuItem>
        </Menu>
        {car.buyer && (
          <RequestPaymentButton
            customer={car.buyer}
            reference={stockNumber}
            type="sales"
            title={[car.year, car.make, car.model].filter(Boolean).join(' ')}
            thumbnail={car.thumbnail}
            defaultAmount={remaining > 0 ? remaining : 0}
            label="Request Deposit"
          />
        )}
      </Box>

      {car.buyer && (
        <Box sx={{ px: 2, pb: 2 }}>
          <SavedCardsList
            customer={car.buyer}
            mode="charge"
            defaultAmount={remaining > 0 ? remaining : 0}
            chargeContext={{
              type: 'sales',
              reference: stockNumber,
              description: `${stockNumber} | ${[car.year, car.make, car.model].filter(Boolean).join(' ')} | sales`,
            }}
            title="Cards on file — charge directly"
          />
          {/* Affidavit authorizing the charge above — signed before it is run. */}
          <Box sx={{ mt: 1.5 }}>
            <CreditCardAffidavitButton
              parentType="car"
              refId={stockNumber}
              customer={car.buyer}
              defaultAmount={remaining > 0 ? remaining : 0}
              vehicleLabel={[car.year, car.make, car.model].filter(Boolean).join(' ')}
            />
          </Box>
        </Box>
      )}

      <DepositModal
        open={depositModalOpen}
        onClose={() => setDepositModalOpen(false)}
        stockNumber={stockNumber}
        onSaved={(saved) => { if (onDepositAdded) onDepositAdded(saved); }}
      />

      <EditDepositModal
        open={!!editDeposit}
        onClose={() => setEditDeposit(null)}
        deposit={editDeposit}
        onSaved={(updated) => { if (onDepositUpdated) onDepositUpdated(updated); }}
      />
    </Box>
  );
}
